import React, { useContext, useEffect, useState, useRef } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { ref, onValue, update } from 'firebase/database';
import { database } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';
import useClickOutside from '../hooks/useClickOutside';
import activityService from '../utils/activityService';
import { subscribePlanCounts, canCreateOrJoinTeam } from '../utils/planGate';
import UpgradeToProModal from './UpgradeToProModal';

const InviteNotifications: React.FC = () => {
  const authContext = useContext(AuthContext);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [teamCount, setTeamCount] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const currentUser = authContext?.user;
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setShowInviteDropdown(false));

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowInviteDropdown(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!currentUser?.email) return;
    const invitesRef = ref(database, 'invites');
    const unsub = onValue(invitesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invitesList = Object.entries(data)
          .map(([id, invite]: [string, any]) => ({ id, ...invite }))
          .filter(invite => invite.email === currentUser.email && invite.status === 'pending');
        setPendingInvites(invitesList);
      } else {
        setPendingInvites([]);
      }
    });
    return () => unsub();
  }, [currentUser?.email]);

  // Keep team count in sync for gating
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = subscribePlanCounts(currentUser.uid, (c) => setTeamCount(c.teamCount));
    return () => { if (unsub) unsub(); };
  }, [currentUser?.uid]);

  const handleAcceptInvite = async (invite: any) => {
    if (!currentUser?.uid || !currentUser?.email) return;
    // Gate acceptance if already in 3 teams
    if (!canCreateOrJoinTeam(teamCount)) {
      setShowUpgrade(true);
      return;
    }

    const teamMembersRef = ref(database, `teams/${invite.teamId}/members`);
    await update(teamMembersRef, {
      [currentUser.uid]: { email: currentUser.email, role: invite.role || 'viewer' }
    });

    const inviteRef = ref(database, `invites/${invite.id}`);
    await update(inviteRef, { status: 'accepted' });
    
    // Log activity for joining team
    await activityService.logTeamJoined(currentUser.uid, invite.teamId, invite.teamName);
    
    setToast(`Joined team ${invite.teamName}`);
    setTimeout(() => setToast(null), 3000);
  };

  const handleDeclineInvite = async (invite: any) => {
    const inviteRef = ref(database, `invites/${invite.id}`);
    await update(inviteRef, { status: 'declined' });
    setToast('Invite declined');
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <BellIcon 
        className="w-5 h-5 lg:w-6 lg:h-6 cursor-pointer hover:text-blue-500 transition-colors" 
        onClick={() => setShowInviteDropdown(v => !v)} 
      />
      {pendingInvites.length > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[16px] flex items-center justify-center">
          {pendingInvites.length}
        </span>
      )}
      {showInviteDropdown && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] transform transition-all duration-200 ease-in-out">
          <div className="p-4 border-b border-gray-100 font-semibold text-gray-800 bg-gray-50 rounded-t-lg">
            Pending Invites ({pendingInvites.length})
          </div>
          {pendingInvites.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              <BellIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              No pending invites.
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {pendingInvites.map(invite => (
                <div key={invite.id} className="p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                  <div className="font-medium text-gray-900 text-sm">{invite.teamName}</div>
                  <div className="text-xs text-gray-500 mt-1">Invited by: {invite.inviter}</div>
                  <div className="flex gap-2 mt-3">
                    <button 
                      className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" 
                      onClick={() => handleAcceptInvite(invite)}
                    >
                      Accept
                    </button>
                    <button 
                      className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1" 
                      onClick={() => handleDeclineInvite(invite)}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {toast && (
        <div className="fixed top-6 right-6 z-[1001] bg-green-500 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-4 animate-fade-in max-w-sm">
          <span className="flex-1">{toast}</span>
          <button 
            onClick={() => setToast(null)} 
            className="ml-2 text-white font-bold hover:text-gray-200 transition-colors"
            aria-label="Close notification"
          >
            &times;
          </button>
        </div>
      )}
      {showUpgrade && (
        <UpgradeToProModal isOpen={true} onClose={() => setShowUpgrade(false)} context="team" />
      )}
    </div>
  );
};

export default InviteNotifications; 